import React, { useRef, useEffect } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { BELL_TOWER_WORLD_POS } from "../constants";

/**
 * BellTollController — lives inside the Canvas.
 *
 *  - Tracks whether the bell tower is currently in the camera frustum
 *    (`visibleRef.current`) by projecting its world position to NDC and
 *    checking the bounding box.
 *  - At the top of every hour, if the bell tower is visible, requests the
 *    audio API to toll (hour % 12 || 12) strikes.
 *  - Also emits a single toll when the bell hotspot is clicked (handled
 *    outside, via the audioApi.toll prop).
 *
 * Props:
 *   audioApi          { toll(count), unlock(), lastTollHourRef }
 *   onVisibilityChange (bool) => void  — optional, fires only when state flips
 *   testTickRef       Ref<{ trigger: () => void }>  — exposes a manual trigger
 *                     for e2e testing (calls toll once if visible).
 */
export default function BellTollController({ audioApi, onVisibilityChange, testTickRef }) {
  const { camera, size } = useThree();
  const visibleRef = useRef(false);
  const projected = useRef(new THREE.Vector3());
  const target = useRef(new THREE.Vector3(...BELL_TOWER_WORLD_POS));
  const lastVisible = useRef(false);
  const accum = useRef(0);

  useFrame((_, delta) => {
    accum.current += delta;
    if (accum.current < 0.25) return; // poll 4×/sec
    accum.current = 0;

    projected.current.copy(target.current);
    projected.current.project(camera);
    const inX = projected.current.x > -1 && projected.current.x < 1;
    const inY = projected.current.y > -1 && projected.current.y < 1;
    const inZ = projected.current.z > -1 && projected.current.z < 1;
    const visible = inX && inY && inZ;
    visibleRef.current = visible;
    if (visible !== lastVisible.current) {
      lastVisible.current = visible;
      onVisibilityChange && onVisibilityChange(visible);
    }
  });

  // Hour-change watcher (outside the render loop).
  useEffect(() => {
    if (!audioApi) return;
    const tick = () => {
      const now = new Date();
      const h = now.getHours();
      const m = now.getMinutes();
      const s = now.getSeconds();
      // Within the first 30s of an hour, treat as "top of the hour".
      const atTop = m === 0 && s < 30;
      if (atTop && audioApi.lastTollHourRef.current !== h && visibleRef.current) {
        audioApi.lastTollHourRef.current = h;
        audioApi.toll((h % 12) || 12);
      }
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => clearInterval(id);
  }, [audioApi]);

  // Expose a manual trigger ref for tests / dev.
  useEffect(() => {
    if (!testTickRef) return;
    testTickRef.current = {
      trigger: () => {
        if (audioApi && visibleRef.current) audioApi.toll(1);
      },
      isVisible: () => visibleRef.current,
    };
  }, [audioApi, testTickRef]);

  return null;
}
